import { useCallback } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { isNull } from "lodash";
import { empty, isAgentIdentity, isOfflinePhotoCaptureFeatureAvailable, isSilentObserver } from "../../shared/utility";
import {
    getParticipantIndex,
    handleSendOfflineModeCommandToGuestUser,
    sendAudioOnOffForParticipantDataTrack,
    sendScreenshareOnOffForParticipantDataTrack,
    sendVideoOnOffForParticipantDataTrack,
} from "../../shared/dataTrackFunction";
import { DEVICE_TYPES, DISABLE, ENABLED, GOOGLE_MAP_LINK, NQ_UNSTABLE_PARAM } from "../../constants/Constants";
import { getNetworkQuality, showMeRoomTypeIsGORoom } from "../Twilio/twilioCallUtility";
import RemoteParticipantsMicrophoneState from "./RemoteParticipantsMicrophoneState";
import RemoteParticipantsVideoState from "./RemoteParticipantsVideoState";
import { setRemoveParticipant } from "../../reducers/common-reducer";
import { MEDIA_PERMISSIONS } from "../../constants/CallSettingConstants";
import { micButtonLDFlagStatusForHostControl } from "../Twilio/AudioCall/AudioCallUtils.js";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PublicVariables } from "../../constants/PublicVariables.js";
import { SCREEN_SHARE_AVAILABILITY_TYPES } from "../../constants/BottomMenuConstants.js";
import googleEvent from "../../services/googleEvent.js";
import { EVENT_NAME } from "../../constants/GTagConstants.js";
import { LogFirebase } from "../../services/firestore.js";
import { FIREBASE_EVENT_CATEGORY } from "../../constants/FirebaseConstants.js";

const RemoteParticipantList = ({ participant, index, localisedValues, newActiveUserList, remoteParticipantesDeviceInfo }) => {
    const dispatch = useDispatch();
    const ldFlags = useFlags();
    const networkQuality = getNetworkQuality(participant?.networkQuality, localisedValues);
    const { nqAvailable } = useSelector((state) => state.commonReducer);
    const { screenshareIdentityParticipantVise } = useSelector((state) => state.twilioEvents);
    const getConnectedPartiName = (identity) => {
        let participantName = getParticipantIndex(identity);
        if (isSilentObserver(identity) && newActiveUserList && typeof newActiveUserList[identity] === "number") {
            participantName = "";
        }
        return participantName;
    };

    const disconnectRemoteUser = (participantIdentity) => {
        const removeParticipantData = {
            isRemoveAllModalOpen: false,
            isRemoveModalOpen: true,
            identity: participantIdentity,
            name: participant.isSilentObserver ? getSilentObserverName() : getConnectedPartiName(participantIdentity),
        };
        LogFirebase(FIREBASE_EVENT_CATEGORY.HOST_CONTROLLED_PARTICIPANTS, "REMOVE_USER_BY_HOST_TAPPED", {
            removeParticipantData: removeParticipantData,
        });
        googleEvent.logEvent(EVENT_NAME.REMOVE_USER_BY_HOST_TAPPED);
        dispatch(setRemoveParticipant(removeParticipantData));
    };

    const handleOfflineMode = (participantIdentity) => {
        googleEvent.logEvent(EVENT_NAME.OFFLINE_MODE_TAPPED);
        LogFirebase("OfflineMode", "for_Guest_user_start_offline_mode_from_agent", {
            identity: participantIdentity,
        });
        handleSendOfflineModeCommandToGuestUser(participantIdentity);
    };

    const getSilentObserverName = () => {
        const name = getConnectedPartiName(participant.identity);
        const index = participant?.isSilentObserverindex > 1 ? ` ${participant.isSilentObserverindex}` : "";

        return `${name ? `${name} (` : ""}${localisedValues?.observer_lable}${index}${name ? ")" : ""}`;
    };

    const startStopVideoForRemoteUserByHost = (participant, isVideoEnabled, videoPermission) => {
        if (videoPermission === "blocked") {
            return;
        }
        LogFirebase(
            FIREBASE_EVENT_CATEGORY.HOST_CONTROLLED_PARTICIPANTS,
            isVideoEnabled ? "TURN_CAM_OFF_BY_HOST_TAPPED" : "ASK_TO_TURN_CAM_ON_BY_HOST_TAPPED",
            {
                isVideoEnabled: isVideoEnabled,
                participant: participant?.identity,
            }
        );
        googleEvent.logEvent(isVideoEnabled ? EVENT_NAME.TURN_CAM_OFF_BY_HOST_TAPPED : EVENT_NAME.ASK_TO_TURN_CAM_ON_BY_HOST_TAPPED);
        sendVideoOnOffForParticipantDataTrack(isVideoEnabled ? DISABLE : ENABLED, participant.identity);
    };

    const startScreenshareForRemoteUserByHost = (identity) => {
        googleEvent.logEvent(EVENT_NAME.ASK_TO_SHARE_SCREEN_BY_HOST_TAPPED);
        LogFirebase(FIREBASE_EVENT_CATEGORY.HOST_CONTROLLED_PARTICIPANTS, "ASK_TO_SHARE_SCREEN_BY_HOST_TAPPED", "true");
        sendScreenshareOnOffForParticipantDataTrack(ENABLED, identity);
    };

    const muteUnmuteAudioForRemoteUserByHost = (participant, isAudioEnabled, audioPermission) => {
        if (audioPermission === "blocked") {
            return;
        }
        LogFirebase(
            FIREBASE_EVENT_CATEGORY.HOST_CONTROLLED_PARTICIPANTS,
            isAudioEnabled ? "TURN_MIC_OFF_BY_HOST_TAPPED" : "ASK_TO_TURN_MIC_ON_BY_HOST_TAPPED",
            {
                isAudioEnabled: isAudioEnabled,
                participant: participant?.identity,
            }
        );
        googleEvent.logEvent(isAudioEnabled ? EVENT_NAME.TURN_MIC_OFF_BY_HOST_TAPPED : EVENT_NAME.ASK_TO_TURN_MIC_ON_BY_HOST_TAPPED);
        sendAudioOnOffForParticipantDataTrack(isAudioEnabled ? DISABLE : ENABLED, participant.identity);
    };

    const screenShareStatusIdentityVise = useCallback(
        (identity) =>
            Array.isArray(screenshareIdentityParticipantVise) && screenshareIdentityParticipantVise.some((user) => user.identity === identity),
        [screenshareIdentityParticipantVise]
    );

    const checkIsiOSScreenShareUser = useCallback(
        (identity) => {
            if (!Array.isArray(screenshareIdentityParticipantVise)) {
                return false;
            }
            return screenshareIdentityParticipantVise.some((user) => user.identity === identity && user.isiOSScreenShareUser === ENABLED);
        },
        [screenshareIdentityParticipantVise]
    );

    const checkIsAskShareScreenBtnFeatureAvailable = (identity) => {
        const SHOWMESCREENSHARE = PublicVariables.featureFlag?.SHOWMESCREENSHARE;
        return (
            !isSilentObserver(identity) &&
            PublicVariables.featureFlag?.ENABLESCREENSHARE !== SCREEN_SHARE_AVAILABILITY_TYPES.offForAllParticipant &&
            (SHOWMESCREENSHARE === SCREEN_SHARE_AVAILABILITY_TYPES.availableForAllParticipant ||
                (!isAgentIdentity(identity) && SHOWMESCREENSHARE === SCREEN_SHARE_AVAILABILITY_TYPES.availableForGuest))
        );
    };

    const checkIsMobileDeviceOnUserDeviceType = (deviceType) => {
        /* Screen share option is supported when *GO ROOM* is present for `DEVICE_TYPES.ANDROID_APP`, so do not include it here. */
        return [DEVICE_TYPES.ANDROID_BROWSER, DEVICE_TYPES.IOS_BROWSER, DEVICE_TYPES.IOS_APP].includes(deviceType);
    };

    const checkIsMobileAPP = (deviceType) => {
        return [DEVICE_TYPES.ANDROID_APP, DEVICE_TYPES.IOS_APP].includes(deviceType);
    };

    const isAskShareScreenBtnNotShowOnGORoom = (participant) => {
        return (
            !isSilentObserver(participant.identity) &&
            !isAgentIdentity(participant.identity) &&
            checkIsMobileDeviceOnUserDeviceType(participant.device_type) &&
            showMeRoomTypeIsGORoom(PublicVariables.featureFlag)
        );
    };

    const isAskShareScreenBtnAvailable = (participant) => {
        if (checkIsAskShareScreenBtnFeatureAvailable(participant.identity) && !isAskShareScreenBtnNotShowOnGORoom(participant)) {
            return true;
        }
        return false;
    };

    return (
        <div
            className={clsx("accordion-item participant-box", {
                "silent-observer-participant": participant.isSilentObserver,
                "participant-no-network": !nqAvailable,
            })}
            data-testid='participant-box'
        >
            <div className='participant-head' id={`participant-heading-${index + 2}`}>
                <div className='participant-head-in'>
                    {nqAvailable && (
                        <div
                            className='participant-network-out'
                            data-bs-toggle='collapse'
                            data-bs-target={`#participant-collapse-${index + 2}`}
                            aria-expanded='true'
                            aria-controls={`participant-collapse-${index + 2}`}
                        >
                            <div className='participant-network custom-tooltip right-tooltip tooltip-auto-width m-hide'>
                                {(remoteParticipantesDeviceInfo[participant?.identity]?.isAudioEnabled ||
                                    remoteParticipantesDeviceInfo[participant?.identity]?.isVideoEnabled) && (
                                    <div className='session-network'>
                                        {isNull(networkQuality?.percentage) && <span className='for-close-icon icon-close'></span>}
                                        <div className='for-base-icon'>
                                            <span className='for-icon icon-network'></span>
                                        </div>
                                        {participant?.networkQuality >= NQ_UNSTABLE_PARAM && (
                                            <div className='for-over-icon'>
                                                <span
                                                    className='for-icon icon-network'
                                                    style={{
                                                        width: networkQuality?.percentage || "0%",
                                                    }}
                                                ></span>
                                            </div>
                                        )}
                                        {!isNull(participant?.networkQuality) && participant?.networkQuality < NQ_UNSTABLE_PARAM && (
                                            <div className='for-over-icon'>
                                                <span className='for-icon icon-network danger-text'></span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!remoteParticipantesDeviceInfo[participant?.identity]?.isAudioEnabled &&
                                    !remoteParticipantesDeviceInfo[participant?.identity]?.isVideoEnabled && (
                                        <>
                                            <div className='session-network'>
                                                <span className='for-close-icon icon-close'></span>
                                                <div className='for-base-icon'>
                                                    <span className='for-icon icon-network'></span>
                                                </div>
                                                <div className='for-over-icon'>
                                                    <span
                                                        className='for-icon icon-network'
                                                        style={{
                                                            width: "0%",
                                                        }}
                                                    ></span>
                                                </div>
                                            </div>
                                            <div className='custom-tooltip-content text-nowrap'>
                                                <div className='custom-tooltip-in'>
                                                    <p>{localisedValues?.network_quality_not_available}</p>
                                                </div>
                                            </div>
                                        </>
                                    )}
                            </div>
                        </div>
                    )}
                    <div
                        className='participant-head-left'
                        data-bs-toggle='collapse'
                        data-bs-target={`#participant-collapse-${index + 2}`}
                        aria-expanded='true'
                        aria-controls={`participant-collapse-${index + 2}`}
                    >
                        {!participant.isSilentObserver && <div className='participant-info-name'>{getConnectedPartiName(participant.identity)}</div>}
                        {participant.isSilentObserver && (
                            <div className='participant-info-name'>
                                <span>{getSilentObserverName()}</span>
                            </div>
                        )}
                    </div>
                    <div className='participant-head-right'>
                        {participant.batteryVal && (
                            <div
                                className={clsx(
                                    "participant-action-info participant-battery-info custom-tooltip tooltip-auto-width m-hide",
                                    participant.isBatteryLow == "0" && "battery-normal"
                                )}
                                data-testid='icon-battery'
                            >
                                <div className='battery-info-in'>
                                    <div className='battery-text' data-testid='battery-val'>
                                        {participant.batteryVal}
                                    </div>
                                    <div className='participant-action-icon '>
                                        <span className='for-icon icon-low-battery'></span>
                                    </div>
                                </div>
                                <div className='custom-tooltip-content text-nowrap'>
                                    <div className='custom-tooltip-in'>
                                        <p>{localisedValues?.batteryinfo_tooltip}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <RemoteParticipantsMicrophoneState
                            isUserAudioEnabled={remoteParticipantesDeviceInfo[participant.identity]?.isAudioEnabled}
                            audioPermission={participant.audioPermission}
                            localisedValues={localisedValues}
                            userIdentity={participant.identity}
                        />

                        {!participant?.isSilentObserver && (
                            <RemoteParticipantsVideoState
                                isUserVideoEnabled={remoteParticipantesDeviceInfo[participant.identity]?.isVideoEnabled}
                                videoPermission={participant.videoPermission}
                                localisedValues={localisedValues}
                            />
                        )}

                        {!checkIsiOSScreenShareUser(participant.identity) && (
                            <div className='participants-control-dropdown own-participants-control'>
                                <div className='dropdown custom-dropdown'>
                                    <button
                                        className='btn dropdown-toggle triangle-none participants-control-btn'
                                        type='button'
                                        id='participants-control-dropdown'
                                        data-bs-toggle='dropdown'
                                        aria-expanded='false'
                                    >
                                        <span className='for-icon icon-more'></span>
                                    </button>
                                    <div className='dropdown-menu right' aria-labelledby='participants-control-dropdown'>
                                        <div className='dropdown-menu-in'>
                                            <ul>
                                                <li>
                                                    <div className='dropdown-item-div'>
                                                        <button
                                                            className='dropdown-item participants-control-action-btn'
                                                            onClick={() =>
                                                                muteUnmuteAudioForRemoteUserByHost(
                                                                    participant,
                                                                    remoteParticipantesDeviceInfo[participant.identity]?.isAudioEnabled,
                                                                    participant.audioPermission
                                                                )
                                                            }
                                                            disabled={
                                                                participant?.audioPermission == MEDIA_PERMISSIONS.BLOCKED_VALUE ||
                                                                !micButtonLDFlagStatusForHostControl(participant.identity, ldFlags?.micButtonStatus)
                                                            }
                                                        >
                                                            <span
                                                                className={clsx(
                                                                    remoteParticipantesDeviceInfo[participant.identity]?.isAudioEnabled
                                                                        ? "for-icon icon-mute-stroke"
                                                                        : "for-icon icon-mic-on-stroke"
                                                                )}
                                                            ></span>
                                                            <span className='for-text'>
                                                                {remoteParticipantesDeviceInfo[participant.identity]?.isAudioEnabled
                                                                    ? localisedValues?.turn_mic_off
                                                                    : localisedValues?.ask_to_turn_mic_on}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </li>
                                                {!isSilentObserver(participant.identity) && (
                                                    <li>
                                                        <div className='dropdown-item-div'>
                                                            <button
                                                                className='dropdown-item participants-control-action-btn'
                                                                onClick={() =>
                                                                    startStopVideoForRemoteUserByHost(
                                                                        participant,
                                                                        remoteParticipantesDeviceInfo[participant.identity]?.isVideoEnabled,
                                                                        participant.videoPermission
                                                                    )
                                                                }
                                                                disabled={
                                                                    participant?.videoPermission == MEDIA_PERMISSIONS.BLOCKED_VALUE ||
                                                                    PublicVariables.featureFlag?.LIVEVIDEO !== ENABLED
                                                                }
                                                            >
                                                                <span
                                                                    className={clsx(
                                                                        remoteParticipantesDeviceInfo[participant.identity]?.isVideoEnabled
                                                                            ? "for-icon icon-video-off-stroke"
                                                                            : "for-icon icon-video-on-stroke"
                                                                    )}
                                                                ></span>
                                                                <span className='for-text'>
                                                                    {remoteParticipantesDeviceInfo[participant.identity]?.isVideoEnabled
                                                                        ? localisedValues?.turn_cam_off
                                                                        : localisedValues?.ask_to_turn_cam_on}
                                                                </span>
                                                            </button>
                                                        </div>
                                                    </li>
                                                )}
                                                {!screenShareStatusIdentityVise(participant.identity) &&
                                                    isAskShareScreenBtnAvailable(participant) && (
                                                        <li>
                                                            <div className='dropdown-item-div'>
                                                                <button
                                                                    className='dropdown-item participants-control-action-btn'
                                                                    onClick={() => startScreenshareForRemoteUserByHost(participant.identity)}
                                                                >
                                                                    <span className='for-icon icon-share-screen'></span>
                                                                    <span className='for-text'>{localisedValues?.ask_to_share_screen}</span>
                                                                </button>
                                                            </div>
                                                        </li>
                                                    )}
                                                <li>
                                                    <div className='dropdown-item-div'>
                                                        <button
                                                            className='dropdown-item participants-control-action-btn'
                                                            onClick={() => disconnectRemoteUser(participant.identity)}
                                                        >
                                                            <span className='for-icon icon-remove-user'></span>
                                                            <span className='for-text'>{localisedValues?.remove_from_call}</span>
                                                        </button>
                                                    </div>
                                                </li>
                                                {isOfflinePhotoCaptureFeatureAvailable() &&
                                                    !isSilentObserver(participant.identity) &&
                                                    !checkIsMobileAPP(participant.device_type) && (
                                                        <li>
                                                            <div className='dropdown-item-div'>
                                                                <button
                                                                    className='dropdown-item participants-control-action-btn'
                                                                    onClick={() => handleOfflineMode(participant.identity)}
                                                                >
                                                                    <span className='for-icon icon-offline-mode'></span>
                                                                    <span className='for-text'>{localisedValues?.enable_offline_mode}</span>
                                                                </button>
                                                            </div>
                                                        </li>
                                                    )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div
                            className='accordion-header-arrow-out'
                            data-bs-toggle='collapse'
                            data-bs-target={`#participant-collapse-${index + 2}`}
                            aria-expanded='true'
                            aria-controls={`participant-collapse-${index + 2}`}
                        >
                            <div className='accordion-header-arrow'>
                                <span className='for-icon icon-up-arrow'></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                id={`participant-collapse-${index + 2}`}
                className='accordion-collapse collapse show'
                aria-labelledby={`participant-heading-${index + 2}`}
            >
                <div className='accordion-body'>
                    <div className='participant-detail'>
                        {(remoteParticipantesDeviceInfo[participant?.identity]?.isAudioEnabled ||
                            remoteParticipantesDeviceInfo[participant?.identity]?.isVideoEnabled) &&
                            networkQuality?.name && (
                                <div className='participant-detail-row'>
                                    <span className='for-lebel'>{localisedValues?.network_quality}:&nbsp;</span>
                                    <span className='for-value'>{networkQuality?.name}</span>
                                </div>
                            )}
                        {!empty(participant?.latitude) && !empty(participant?.longitude) && (
                            <div className='participant-detail-row participant-location-row'>
                                <span className='for-lebel'>{localisedValues?.loc}:&nbsp;</span>
                                <span className='for-value'>
                                    <button
                                        className='btn btn-link'
                                        onClick={() => window.open(GOOGLE_MAP_LINK + participant?.latitude + "," + participant.longitude)}
                                    >
                                        {participant.latitude.toFixed(4)}, {participant.longitude.toFixed(4)}
                                        <span className='btn-icon icon-new-tab'></span>
                                    </button>
                                </span>
                            </div>
                        )}
                        <div className='participant-detail-row'>
                            {participant?.webDevice && participant.webDevice + " "}
                            {participant?.browserName && participant.browserName + " "}
                            {participant?.appVersion && participant.appVersion}
                        </div>
                        {participant?.networkType && (
                            <div className='participant-detail-row'>
                                <span className='for-lebel'>{localisedValues?.network_type}:</span>&nbsp;
                                <span className='for-value'>{participant?.networkType}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

RemoteParticipantList.propTypes = {
    participant: PropTypes.any,
    index: PropTypes.any,
    newActiveUserList: PropTypes.any,
    remoteParticipantesDeviceInfo: PropTypes.any,
    localisedValues: PropTypes.any,
};
export default RemoteParticipantList;
